import React, { useState } from 'react';
import { Button, ButtonGroup, MenuItem, Popover } from '@mui/material';
import useTranslate from '../../customHooks/useTranslate';
import Icon from '../../muiComponents/Icon';
import { CalendarView } from '../../types/calendar/calendarTypes';
import Tooltip from '../../muiComponents/Tooltip';

interface HeaderCalendarProps {
    currentPeriodTitle: string;
    onViewChange: (view: CalendarView) => void;
    onPrev: () => void;
    onNext: () => void;
    onToday: () => void;
    onGoToDate: (date: string | Date) => void;
    activeView: CalendarView;
    periodOptions: { label: string; value: string }[];
    viewButtons: { view: CalendarView; label: string }[];
    titleButtonbarEnd?: React.ReactNode;
}

const HeaderCalendar = (props: HeaderCalendarProps) => {
    const {
        currentPeriodTitle,
        onViewChange,
        onPrev,
        onNext,
        onToday,
        onGoToDate,
        activeView,
        periodOptions,
        viewButtons,
        titleButtonbarEnd,
    } = props;
    const translate = useTranslate();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    return (
        <div className='flex flex-between flex-align-baseline'>
            <div
                className='flex flex-start gap-10 flex-align-center'
                style={{ flex: 1, minWidth: 0 }}
            >
                <Button
                    onClick={e =>
                        periodOptions.length && setAnchorEl(e.currentTarget)
                    }
                    endIcon={
                        periodOptions.length && (
                            <Tooltip
                                titleText='show_periods_with_results'
                                placement='bottom'
                            >
                                <span className='flex-center flex-align-center'>
                                    <Icon iconName='ExpandMore' />
                                </span>
                            </Tooltip>
                        )
                    }
                    sx={{
                        color: 'var(--text)',
                        textTransform: 'capitalize',
                        fontSize: '18px',
                        fontWeight: 700,
                    }}
                >
                    {currentPeriodTitle}
                </Button>
                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                >
                    <Tooltip
                        titleText='periods_with_results'
                        placement='bottom'
                    >
                        <div>
                            {periodOptions.map(option => (
                                <MenuItem
                                    key={option.value}
                                    onClick={() => {
                                        onGoToDate(option.value);
                                        setAnchorEl(null);
                                    }}
                                    sx={{ textTransform: 'capitalize' }}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </div>
                    </Tooltip>
                </Popover>
                {titleButtonbarEnd}
            </div>
            <div
                className='flex flex-center gap-10 flex-align-center'
                style={{ flex: 1, minWidth: 0 }}
            >
                <ButtonGroup
                    variant='contained'
                    sx={{
                        '& .MuiButtonGroup-firstButton': {
                            borderColor: 'var(--border)',
                        },
                    }}
                >
                    {viewButtons.map(button => (
                        <Button
                            sx={{
                                color: ' var(--text)',
                                border: '1px solid var(--border)',
                                backgroundColor:
                                    activeView === button.view
                                        ? 'var(--background)'
                                        : 'var(--white)',
                                padding: '4px 12px',
                            }}
                            className='fc-header__button'
                            key={button.view}
                            onClick={() => onViewChange(button.view)}
                        >
                            {translate(button.label)}
                        </Button>
                    ))}
                </ButtonGroup>
            </div>
            <div
                className='flex flex-end gap-10 flex-align-center'
                style={{ flex: 1, minWidth: 0 }}
            >
                <ButtonGroup
                    variant='contained'
                    sx={{
                        '& .MuiButtonGroup-firstButton, & .MuiButtonGroup-middleButton, & .MuiButtonGroup-lastButton':
                            {
                                borderColor: 'var(--border)',
                            },
                    }}
                >
                    <Button
                        onClick={onPrev}
                        sx={{
                            color: ' var(--text)',
                            border: '1px solid var(--border)',
                            backgroundColor: 'var(--white)',
                            padding: '4px 12px',
                        }}
                    >
                        <Icon iconName='ChevronLeft' />
                    </Button>
                    <Button
                        onClick={onToday}
                        sx={{
                            color: ' var(--text)',
                            border: '1px solid var(--border)',
                            backgroundColor: 'var(--white)',
                            padding: '4px 12px',
                        }}
                    >
                        {translate('today')}
                    </Button>
                    <Button
                        onClick={onNext}
                        sx={{
                            color: ' var(--text)',
                            border: '1px solid var(--border)',
                            backgroundColor: 'var(--white)',
                            padding: '4px 12px',
                        }}
                    >
                        <Icon iconName='ChevronRight' />
                    </Button>
                </ButtonGroup>
            </div>
        </div>
    );
};

export default HeaderCalendar;
